// frontend/admin-portal/src/pages/Requisitions.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  PageHeader,
  Card,
  Button,
  Table,
  Alert,
  Input,
} from "@fiberhome/shared-ui";
import { requisitionService } from "../services/requisitionService";
import RequisitionModal from "../components/procurement/RequisitionModal";
import RequisitionStatusBadge from "../components/procurement/RequisitionStatusBadge";

export default function Requisitions() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    search: "",
    status: "",
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      for (const [k, v] of Object.entries(filters)) {
        if (v !== "" && v != null) params[k] = v;
      }
      const { items, pagination } = await requisitionService.list(params);
      setItems(items);
      setPagination(pagination);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const handleNew = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const handleEdit = (r) => {
    setEditing(r);
    setModalOpen(true);
  };
  const handleSaved = () => {
    setRefreshKey((k) => k + 1);
    load();
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete ${r.code}? This cannot be undone.`)) return;
    try {
      await requisitionService.remove(r.id);
      load();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  const columns = [
    {
      key: "code",
      label: "Requisition",
      render: (r) => (
        <div
          onClick={() => navigate(`/requisitions/${r.id}`)}
          style={{ cursor: "pointer" }}
        >
          <div style={{ fontWeight: 600 }}>{r.code}</div>
          <div className="text-muted" style={{ fontSize: 12 }}>
            {r.title}
          </div>
        </div>
      ),
    },
    {
      key: "itemCount",
      label: "Items",
      render: (r) => <strong>{r.itemCount}</strong>,
    },
    {
      key: "rfqCount",
      label: "RFQs",
      render: (r) => (
        <div>
          <strong>{r.rfqCount}</strong>
          {r.quotedCount > 0 && (
            <span className="text-muted" style={{ fontSize: 12 }}>
              {" "}
              · {r.quotedCount} quoted
            </span>
          )}
        </div>
      ),
    },
    {
      key: "neededBy",
      label: "Needed By",
      render: (r) => r.neededBy || <span className="text-muted">—</span>,
    },
    {
      key: "priority",
      label: "Priority",
      render: (r) => (
        <span style={{ textTransform: "capitalize" }}>{r.priority}</span>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (r) => <RequisitionStatusBadge status={r.status} />,
    },
    {
      key: "actions",
      label: "",
      width: 220,
      render: (r) => (
        <div className="d-flex gap-1">
          <Button
            size="sm"
            variant="primary"
            onClick={() => navigate(`/requisitions/${r.id}`)}
          >
            Open
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleEdit(r)}
            disabled={r.status !== "draft"}
          >
            Edit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleDelete(r)}
            disabled={r.status !== "draft"}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Purchase Requisitions"
        subtitle="Items you want to buy — get quotes from multiple suppliers"
        breadcrumbs={["Home", "Procurement", "Requisitions"]}
        actions={
          <Button variant="primary" onClick={handleNew}>
            + New Requisition
          </Button>
        }
      />

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <Card className="mb-3">
        <div className="fh-supplier-filters">
          <Input
            placeholder="Search by code or title..."
            value={filters.search}
            onChange={(e) =>
              setFilters({ ...filters, search: e.target.value, page: 1 })
            }
          />
          <select
            className="fh-input"
            value={filters.status}
            onChange={(e) =>
              setFilters({ ...filters, status: e.target.value, page: 1 })
            }
          >
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="quoting">Quoting</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </Card>

      <Card
        title={`Requisitions (${pagination.total})`}
        actions={
          <div className="fh-products-toolbar__pager">
            <Button
              size="sm"
              variant="ghost"
              disabled={pagination.page <= 1}
              onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
            >
              Prev
            </Button>
            <span className="text-muted" style={{ fontSize: 12 }}>
              Page {pagination.page} / {pagination.pages || 1}
            </span>
            <Button
              size="sm"
              variant="ghost"
              disabled={pagination.page >= pagination.pages}
              onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
            >
              Next
            </Button>
          </div>
        }
      >
        <Table
          columns={columns}
          data={items}
          loading={loading}
          emptyMessage="No requisitions yet"
        />
      </Card>

      <RequisitionModal
        open={modalOpen}
        requisition={editing}
        onClose={() => setModalOpen(false)}
        onSaved={handleSaved}
      />
    </>
  );
}
